/**
 * P9-2「図鑑の Supabase 台帳化」: クライアント同期層。
 *
 * localStorage 図鑑（utils/collection.ts）を「ローカルキャッシュ + オフライン送信キュー（outbox）」に
 * 降格し、Worker の台帳 API（POST /api/v1/zukan/clears / GET /api/v1/zukan?owner=）と同期する。
 *
 * 最重要要件は graceful degradation:
 * - 本番 Worker がまだこの API を持たない / オフライン / タイムアウト / Pages の SPA フォールバックが
 *   HTML を返す — いずれの場合も**黙って「ローカルのみモード」に降格**し、outbox は保持して次回再試行する。
 * - 図鑑 UI は常に localStorage 分を即表示する（サーバ応答を待たない）。エラーを UI に出さない。
 *
 * 設計（提案書 §8.3 / タスク契約で確定した merge の正）:
 * - 送信: recordTsukumogami が「実際にカウントした」記録だけを outbox（localStorage
 *   `player_zukan_outbox`）へ積み、オンライン時に POST で flush する。サーバは run_id UNIQUE の
 *   冪等 insert なので再送は常に安全（accepted:false の no-op になる）。
 * - 受信 merge（サーバ優先）: サーバに存在する key は **clearCount = サーバ行数を正** とする。
 *   max(server, local) にはせず、ローカルのみの周回分（outbox 未送信分）もここで加算しない
 *   — 未送信分は flush が成功した時点でサーバ行数として反映されるため、今加算すると flush 後に
 *   二重計上される。この「サーバ行数のみを正とする」規約が二重計上の構造遮断になる
 *   （代償として、送信失敗が続く間はサーバ既知エントリの表示 clearCount が未送信分だけ小さく
 *   見える瞬間があり得る。台帳の信頼性 > 一時的な見かけ、で許容）。
 * - endingTypes は和集合（サーバの到達順を先頭に、ローカルのみの結末を後置）。
 * - ローカルにしかないエントリはそのまま残す（outbox 経由でサーバへ回収される）。
 * - サーバにしかないエントリ（機種変・localStorage 消失後の復元）は、key が `name:` 規約のとき
 *   のみ名前を復元して再構築する。`scenario:` キーは付喪神メタを行に複製しない設計（§8.3-1）のため
 *   名前を解決できず、MVP ではスキップする（P9-3 のマニフェスト昇格で解決予定。現行の公開 Player は
 *   EndingView が scenarioId を渡さないため実キーは常に `name:` — 実害なし）。
 * - ownerKey は既存 `player_device_key` 規約（utils/deviceKey.ts）をそのまま使う。
 * - API base は bootstrap.ts の live 経路と同一規約（`VITE_API_URL`・未設定は同一 origin 相対）。
 */

import { ensureDeviceKey } from './deviceKey';
import {
  getCollection,
  recordTsukumogami,
  replaceCollection,
  type CollectionEntry,
  type RecordTsukumogamiInput
} from './collection';

const OUTBOX_KEY = 'player_zukan_outbox';
const REQUEST_TIMEOUT_MS = 8000;
/**
 * outbox の上限。Worker 不在が長期化しても localStorage を無限に太らせない。
 * 超過時は最古を落とす（最新のクリアを優先保持。200 周分未送信は想定外の異常系）。
 */
const MAX_OUTBOX_ITEMS = 200;

/** POST /api/v1/zukan/clears の送信 1 件分（Worker 側 Zod schema と対応）。 */
export interface ZukanOutboxItem {
  ownerKey: string;
  tsukumogamiKey: string;
  runId: string;
  endingType?: string;
  scenarioId?: string;
  /** ISO 8601 文字列（クリア実時刻。オフライン後送でも実時刻を保つ）。 */
  clearedAt: string;
}

/** GET /api/v1/zukan の集計エントリ（Worker zukan.ts の ZukanLedgerEntry と対応）。 */
export interface ZukanServerEntry {
  tsukumogamiKey: string;
  clearCount: number;
  endingTypes: string[];
  scenarioIds?: string[];
  firstClearedAt?: string;
  lastClearedAt?: string;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

/** bootstrap.ts と同一規約: VITE_API_URL（未設定は '' = 同一 origin 相対）。 */
function resolveApiBase(): string {
  try {
    const base = import.meta.env?.VITE_API_URL as string | undefined;
    return typeof base === 'string' ? base.replace(/\/+$/, '') : '';
  } catch {
    return '';
  }
}

/** 降格ログはセッション中 1 回だけ（console を汚さない。error ではなく info）。 */
let degradeLogged = false;
function logLocalOnly(reason: string): void {
  if (degradeLogged) return;
  degradeLogged = true;
  try {
    console.info(`[zukan-sync] 台帳サーバに接続できないため、図鑑はローカル記録のみで動作します（${reason}）`);
  } catch {
    // ignore
  }
}

// =============================================================================
// Outbox（localStorage 送信キュー）
// =============================================================================

function isValidOutboxItem(value: unknown): value is ZukanOutboxItem {
  if (value == null || typeof value !== 'object') return false;
  const item = value as ZukanOutboxItem;
  return (
    typeof item.ownerKey === 'string' &&
    item.ownerKey.length > 0 &&
    typeof item.tsukumogamiKey === 'string' &&
    item.tsukumogamiKey.length > 0 &&
    typeof item.runId === 'string' &&
    item.runId.length > 0 &&
    typeof item.clearedAt === 'string' &&
    item.clearedAt.length > 0
  );
}

/** outbox を読む。破損・非配列・不正エントリは捨てて健全な配列だけ返す（throw しない）。 */
export function readZukanOutbox(): ZukanOutboxItem[] {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(OUTBOX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidOutboxItem);
  } catch {
    return [];
  }
}

function writeZukanOutbox(items: ZukanOutboxItem[]): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(OUTBOX_KEY, JSON.stringify(items));
  } catch {
    // 容量超過 / アクセス不可は黙って諦める（次の記録時に再試行される）。
  }
}

/**
 * outbox へ 1 件積む。runId 重複は積まない（サーバの run_id UNIQUE と同じ粒度の冪等）。
 * 上限超過時は最古を落とす。
 */
export function enqueueZukanClear(item: ZukanOutboxItem): void {
  if (!isBrowser()) return;
  const outbox = readZukanOutbox();
  if (outbox.some((existing) => existing.runId === item.runId)) return;
  const next = [...outbox, item].slice(-MAX_OUTBOX_ITEMS);
  writeZukanOutbox(next);
}

// =============================================================================
// HTTP（fetch はモック可能なように global を都度参照する）
// =============================================================================

async function fetchWithTimeout(url: string, init: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

type PostOutcome = 'sent' | 'drop' | 'retry';

/**
 * 1 件 POST する。
 * - 2xx + JSON success:true → 'sent'（accepted:false の冪等 no-op も送達成功として dequeue）
 * - 400 → 'drop'（不正 payload は再送しても永遠に失敗する毒キューになるため破棄して前進）
 * - それ以外（404=Worker 未対応 / 405=Pages 静的 / 5xx / ネットワーク / タイムアウト /
 *   2xx だが JSON でない=SPA フォールバック）→ 'retry'（保持して次回）
 */
async function postZukanClear(item: ZukanOutboxItem): Promise<PostOutcome> {
  try {
    const res = await fetchWithTimeout(`${resolveApiBase()}/api/v1/zukan/clears`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item)
    });
    if (res.status === 400) {
      logLocalOnly('不正な送信データを破棄');
      return 'drop';
    }
    if (!res.ok) return 'retry';
    const body = (await res.json().catch(() => null)) as { success?: boolean } | null;
    if (!body || body.success !== true) return 'retry';
    return 'sent';
  } catch {
    return 'retry';
  }
}

let flushInFlight = false;

/**
 * outbox を先頭から順に POST で flush する。
 * - 'sent' / 'drop' は dequeue して前進。
 * - 'retry' に当たった時点で中断（その item 以降は保持＝ローカルのみモードへ降格・次回再試行）。
 * - 冪等（run_id UNIQUE）なので、途中クラッシュ→再送でも二重計上しない。
 * - 多重呼び出しはガード（同時 flush で同じ item を二重 POST しない）。
 */
export async function flushZukanOutbox(): Promise<{ sent: number; remaining: number }> {
  if (!isBrowser()) return { sent: 0, remaining: 0 };
  if (flushInFlight) return { sent: 0, remaining: readZukanOutbox().length };
  flushInFlight = true;
  try {
    let outbox = readZukanOutbox();
    let sent = 0;
    while (outbox.length > 0) {
      const outcome = await postZukanClear(outbox[0]);
      if (outcome === 'retry') {
        logLocalOnly('送信を保留');
        break;
      }
      if (outcome === 'sent') sent += 1;
      outbox = outbox.slice(1);
      writeZukanOutbox(outbox);
    }
    return { sent, remaining: outbox.length };
  } finally {
    flushInFlight = false;
  }
}

// =============================================================================
// 記録（recordTsukumogami のラッパー）
// =============================================================================

/**
 * 図鑑へ記録し、実際にカウントが動いた場合のみサーバ台帳への送信キューに積んで flush を試みる。
 * - ローカル記録は従来どおり同期・即時（recordTsukumogami）。UI はこの時点で最新。
 * - runId の無い記録は台帳へ送らない（冪等キーなしの行は台帳の信頼性を壊すため。実運用では
 *   App 層が常に playthroughId / live runId を渡すので通常発生しない）。
 * - flush は fire-and-forget（失敗しても outbox に残り、次回の記録 or 図鑑オープン時に再送）。
 */
export function recordTsukumogamiWithSync(input: RecordTsukumogamiInput): void {
  const result = recordTsukumogami(input);
  if (!result.recorded || !result.key) return;
  const runId = (input.runId ?? '').trim();
  if (!runId) return;
  enqueueZukanClear({
    ownerKey: ensureDeviceKey(),
    tsukumogamiKey: result.key,
    runId,
    endingType: input.endingType,
    scenarioId: input.scenarioId,
    clearedAt: new Date(result.recordedAt ?? Date.now()).toISOString()
  });
  void flushZukanOutbox();
}

// =============================================================================
// 受信（GET → merge）
// =============================================================================

function isValidServerEntry(value: unknown): value is ZukanServerEntry {
  if (value == null || typeof value !== 'object') return false;
  const entry = value as ZukanServerEntry;
  return (
    typeof entry.tsukumogamiKey === 'string' &&
    entry.tsukumogamiKey.length > 0 &&
    typeof entry.clearCount === 'number' &&
    Number.isFinite(entry.clearCount) &&
    entry.clearCount > 0 &&
    Array.isArray(entry.endingTypes)
  );
}

/** サーバ台帳を取得する。失敗（非 2xx / 非 JSON / ネットワーク / タイムアウト）は null（ローカルのみモード）。 */
export async function fetchZukanLedger(ownerKey: string): Promise<ZukanServerEntry[] | null> {
  try {
    const res = await fetchWithTimeout(
      `${resolveApiBase()}/api/v1/zukan?owner=${encodeURIComponent(ownerKey)}`,
      { method: 'GET' }
    );
    if (!res.ok) return null;
    // Pages の SPA フォールバックは未知 GET に 200 + HTML を返し得る → JSON 検証で弾く。
    const body = (await res.json().catch(() => null)) as
      | { success?: boolean; data?: { entries?: unknown } }
      | null;
    if (!body || body.success !== true || !Array.isArray(body.data?.entries)) return null;
    return (body.data!.entries as unknown[]).filter(isValidServerEntry).map((entry) => ({
      tsukumogamiKey: entry.tsukumogamiKey,
      clearCount: Math.floor(entry.clearCount),
      endingTypes: entry.endingTypes.filter((t): t is string => typeof t === 'string' && t.length > 0),
      scenarioIds: Array.isArray(entry.scenarioIds)
        ? entry.scenarioIds.filter((s): s is string => typeof s === 'string' && s.length > 0)
        : [],
      firstClearedAt: typeof entry.firstClearedAt === 'string' ? entry.firstClearedAt : undefined,
      lastClearedAt: typeof entry.lastClearedAt === 'string' ? entry.lastClearedAt : undefined
    }));
  } catch {
    return null;
  }
}

/** `name:<名前>` キーから表示名を復元する（scenario: キーは null＝復元不能）。 */
function nameFromKey(key: string): string | null {
  if (!key.startsWith('name:')) return null;
  const name = key.slice('name:'.length).trim();
  return name.length > 0 ? name : null;
}

function parseIsoMs(value: string | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * サーバ台帳をローカルキャッシュへ merge する純関数（テスト対象の本丸）。
 * merge の正はファイル冒頭コメント参照（サーバ優先・endingTypes 和集合・二重計上防止）。
 */
export function mergeZukanEntries(
  local: CollectionEntry[],
  server: ZukanServerEntry[]
): CollectionEntry[] {
  const result = local.map((entry) => ({ ...entry }));
  const byKey = new Map(result.map((entry) => [entry.key, entry]));

  for (const serverEntry of server) {
    const localEntry = byKey.get(serverEntry.tsukumogamiKey);
    const serverFirstMs = parseIsoMs(serverEntry.firstClearedAt);
    const serverLastMs = parseIsoMs(serverEntry.lastClearedAt);

    if (localEntry) {
      // サーバ優先: clearCount はサーバ行数を正とする（max にしない・未送信分を加算しない）。
      localEntry.clearCount = serverEntry.clearCount;
      // endingTypes 和集合: サーバの到達順を先頭に、ローカルのみの結末（未送信分）を後置。
      const merged = [...serverEntry.endingTypes];
      for (const t of localEntry.endingTypes) {
        if (!merged.includes(t)) merged.push(t);
      }
      localEntry.endingTypes = merged;
      // 時刻は台帳とローカルの min/max（表示用。ローカルに未送信の新しいクリアがあり得るため）。
      if (serverFirstMs !== null) {
        localEntry.firstClearedAt = Math.min(localEntry.firstClearedAt, serverFirstMs);
      }
      if (serverLastMs !== null) {
        localEntry.lastClearedAt = Math.max(localEntry.lastClearedAt, serverLastMs);
      }
      continue;
    }

    // サーバにしかないエントリ（機種変 / localStorage 消失後の復元）。
    const name = nameFromKey(serverEntry.tsukumogamiKey);
    if (!name) continue; // scenario: キーは名前を復元できないため MVP ではスキップ（冒頭コメント参照）。
    const nowMs = Date.now();
    const restored: CollectionEntry = {
      key: serverEntry.tsukumogamiKey,
      name,
      scenarioId: serverEntry.scenarioIds?.[0],
      endingTypes: [...serverEntry.endingTypes],
      // recordedRunIds は空で良い: 冪等の本丸はサーバの run_id UNIQUE。ローカル側ガードは
      // 「この端末でこれから始まる run」にしか要らず、過去 run の id は不要。
      recordedRunIds: [],
      firstClearedAt: serverFirstMs ?? nowMs,
      lastClearedAt: serverLastMs ?? serverFirstMs ?? nowMs,
      clearCount: serverEntry.clearCount
      // description / illustrationAssetId / rarity は行に複製しない設計のため復元不能。
      // 次にこの端末で該当シナリオをクリアした時、recordTsukumogami の upsert が最新値で埋める。
    };
    result.push(restored);
    byKey.set(restored.key, restored);
  }

  return result;
}

// =============================================================================
// オーケストレーション（図鑑オープン時の同期）
// =============================================================================

/**
 * 図鑑を開いた時の同期: outbox flush（ローカルにしかない分の回収）→ GET → merge → キャッシュ更新。
 * サーバへ到達できない場合は null を返す（呼び出し側は何もしない＝ローカル表示のまま）。
 */
export async function syncZukanWithServer(): Promise<CollectionEntry[] | null> {
  if (!isBrowser()) return null;
  try {
    await flushZukanOutbox();
    const server = await fetchZukanLedger(ensureDeviceKey());
    if (server === null) {
      logLocalOnly('台帳の取得に失敗');
      return null;
    }
    const merged = mergeZukanEntries(getCollection(), server);
    replaceCollection(merged);
    return merged;
  } catch {
    return null;
  }
}
